import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import styles from "./SelectPhotoModal.module.css";
import classNames from "classnames";
import { useSelectPhotoModal } from "./useSelectPhotoModal";
import { Plus } from "react-ikonate";

import Button from "../../../shared/Button";
import Loader from "shared/Loader";

const buildTree = (urls, folder) => {
  const tree = { base: [] };

  urls.forEach((url) => {
    if (!url.includes(`/${folder}/`)) return;

    const parts = url.split(`/${folder}/`)[1].split("/");

    if (parts.length === 1 || /^[0-9a-fA-F-]{36}$/.test(parts[0])) {
      tree.base.push(url);
      return;
    }

    let node = tree;

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      const isUUID = /^[0-9a-fA-F-]{36}$/.test(part);
      const isLast = i === parts.length - 1;

      if (isUUID) {
        const nextPart = parts[i + 1];
        if (nextPart) {
          node.base = node.base || [];
          node.base.push(url);
        }
        break;
      } else if (isLast) {
        node.base = node.base || [];
        node.base.push(url);
      } else {
        node[part] = node[part] || { base: [] };
        node = node[part];
      }
    }
  });

  return tree;
};

export function SelectPhotoModal({
  isVisible,
  setIsVisible,
  onSubmit,
  index,
  folder,
}) {
  const imagePreviewRef = useRef(null);

  const {
    uploadImage,
    isLoading,
    images: allImages,
    isLoadingImages,
    refetchImages,
  } = useSelectPhotoModal(folder);

  const [folderStack, setFolderStack] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState();

  const tree = buildTree(allImages || [], folder);

  const currentNode = folderStack.reduce((node, folder) => node[folder], tree);

  const dragOver = (e) => e.preventDefault();

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (err) => reject(err);
    });

  const handleFile = async (file) => {
    if (file && file.type.startsWith("image/")) {
      try {
        const base64 = await fileToBase64(file);
        uploadImage(
          { body: { name: file.name, image: base64, folder } },
          {
            onSuccess: (updatedItem) => {
              setSelectedPhoto(updatedItem.url);
              refetchImages();
            },
            onError: (err) => console.error("Upload failed:", err),
          }
        );
      } catch (err) {
        console.error("Error reading file:", err);
      }
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    handleFile(file);
  };

  const onFileInputChange = (e) => {
    const file = e.target.files[0];
    handleFile(file);
  };

  const onSubmitPhoto = () => {
    onSubmit(selectedPhoto, index);
    setIsVisible(false);
  };

  const renderFolders = (node) =>
    Object.keys(node)
      .filter((k) => k !== "base")
      .map((folder) => (
        <div
          key={folder}
          className={styles.folderItem}
          onClick={() => setFolderStack([...folderStack, folder])}
        >
          📁 {folder}
        </div>
      ));

  const Tooltip = ({ text, children }) => {
    const [visible, setVisible] = useState(false);
    const [coords, setCoords] = useState(null);
    const wrapperRef = useRef(null);

    useEffect(() => {
      if (visible && wrapperRef.current) {
        const rect = wrapperRef.current.getBoundingClientRect();
        setCoords({
          top: rect.top - 25,
          left: rect.left + rect.width / 2,
        });
      }
    }, [visible]);

    return (
      <div
        ref={wrapperRef}
        className={styles.tooltipWrapper}
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
      >
        {children}
        {visible &&
          coords &&
          createPortal(
            <div
              className={styles.tooltip}
              style={{
                position: "fixed",
                top: coords.top,
                left: coords.left,
                transform: "translateX(-50%)",
              }}
            >
              {text}
            </div>,
            document.body
          )}
      </div>
    );
  };

  const renderImages = (node) =>
    (node.base || []).map((url) => {
      const rawName = url.split("/").pop() || "";
      let fileName = decodeURIComponent(rawName.split("?")[0]);

      const dotIndex = fileName.lastIndexOf(".");

      if (dotIndex > 0) {
        fileName = fileName.substring(0, dotIndex);
      }

      return (
        <Tooltip key={url} text={fileName}>
          <img
            src={url}
            className={styles.thumbnail}
            alt={fileName}
            onClick={() => setSelectedPhoto(url)}
            onLoad={(e) => {
              const { naturalWidth, naturalHeight } = e.target;
              if (naturalWidth < 80 || naturalHeight < 80) {
                e.target.classList.add(styles.containThumb);
              }
            }}
          />
        </Tooltip>
      );
    });

  const goUpFolder = () => setFolderStack(folderStack.slice(0, -1));

  return (
    <div
      className={classNames(styles.overlay, { [styles.hidden]: !isVisible })}
    >
      <div className={styles.centeredModal}>
        {isLoading ? (
          <Loader />
        ) : (
          <>
            <div
              className={styles.outerContainer}
              onDragOver={dragOver}
              onDrop={onDrop}
            >
              {selectedPhoto ? (
                <img src={selectedPhoto} className={styles.preview} alt="" />
              ) : (
                <p className={styles.placeholder}>
                  Select a file or drag one here
                </p>
              )}
            </div>

            <div className={styles.btnDiv}>
              <input
                type="file"
                ref={imagePreviewRef}
                className={styles.file}
                onChange={onFileInputChange}
              />
              <Button
                onClick={() => imagePreviewRef.current.click()}
                className={styles.selectBtn}
                icon={<Plus borderwidth={5} />}
              >
                Upload Photo
              </Button>
              {selectedPhoto && (
                <Button onClick={onSubmitPhoto} className={styles.useBtn}>
                  Use This Photo
                </Button>
              )}
              <Button
                onClick={() => setIsVisible(false)}
                className={styles.cancelBtn}
              >
                Cancel
              </Button>
            </div>
            <hr className={styles.hr} />
            {isLoadingImages ? (
              <Loader />
            ) : (
              <div
                className={styles.folderContainer}
                style={{ maxHeight: "300px", overflowY: "auto" }}
              >
                {folderStack.length > 0 && (
                  <Button onClick={goUpFolder} className={styles.backBtn}>
                    Back
                  </Button>
                )}
                <div className={styles.folders}>
                  {renderFolders(currentNode)}
                </div>
                <div className={styles.images}>{renderImages(currentNode)}</div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

SelectPhotoModal.propTypes = {
  currentPhotoSrc: PropTypes.string,
  setCurrentPhotoSrc: PropTypes.func,
  isVisible: PropTypes.bool,
  onSubmit: PropTypes.func,
  setIsVisible: PropTypes.func,
};
